import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Shield, Check, Zap, MessageSquare, Star, Sparkles, Crown, Infinity, ChevronDown, Loader2, Compass, Lock, Eye, TrendingUp, Search, Users, Utensils, Coffee, Scissors, Briefcase, Map, Sparkle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PhoneShot = ({ imageUrl, label, sublabel }) => (
  <div className="flex flex-col items-center">
    <div className="relative w-60 bg-gray-900 rounded-[2.5rem] shadow-2xl border-[6px] border-gray-800 overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-28 h-6 bg-gray-900 rounded-b-2xl z-10"></div>
      <img src={imageUrl} alt={label} loading="lazy" className="w-full h-auto block" />
    </div>
    <h4 className="text-lg font-bold text-gray-900 mt-5">{label}</h4>
    {sublabel && <p className="text-gray-600 text-sm text-center max-w-xs mt-2">{sublabel}</p>}
  </div>
);

const ForBusiness = () => {
  const [searchParams] = useSearchParams();
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [purchaseCount, setPurchaseCount] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');
  const [promoCode, setPromoCode] = useState(null);
  const [isExpired, setIsExpired] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  // Calculate time remaining until December 31, 2027
  useEffect(() => {
    const targetDate = new Date('2027-12-31T23:59:59');
    
    const updateTimer = () => {
      const now = new Date();
      const diff = targetDate - now;
      
      if (diff > 0) {
        setTimeLeft({
          days: Math.floor(diff / (1000 * 60 * 60 * 24)),
          hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((diff % (1000 * 60)) / 1000)
        });
        setIsExpired(false);
      } else {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        setIsExpired(true);
      }
    };
    
    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, []);

  // Fetch purchase count
  useEffect(() => {
    const fetchPurchaseCount = async () => {
      try {
        const response = await fetch(`${API_URL}/api/presale/count`);
        if (response.ok) {
          const data = await response.json();
          setPurchaseCount(data.count || 0);
        }
      } catch (error) {
        console.error('Error fetching purchase count:', error);
      }
    };
    
    fetchPurchaseCount();
  }, []);

  // Check for payment return from Stripe
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      setPaymentStatus('checking');
      setStatusMessage('Checking payment status...');
      pollPaymentStatus(sessionId);
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setPaymentStatus('timeout');
      setStatusMessage('Payment status check timed out. Please check your email for confirmation.');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/checkout-status/${sessionId}`);
      if (!response.ok) throw new Error('Failed to check payment status');

      const data = await response.json();
      
      if (data.payment_status === 'paid') {
        setPaymentStatus('success');
        if (data.promo_code) {
          setPromoCode(data.promo_code);
          setStatusMessage('Payment successful! Your promo code is below.');
        } else {
          setStatusMessage('Payment successful! Thank you for your purchase. You will receive a confirmation email shortly.');
        }
        // Refresh purchase count
        const countResponse = await fetch(`${API_URL}/api/presale/count`);
        if (countResponse.ok) {
          const countData = await countResponse.json();
          setPurchaseCount(countData.count || 0);
        }
        return;
      } else if (data.status === 'expired') {
        setPaymentStatus('expired');
        setStatusMessage('Payment session expired. Please try again.');
        return;
      }

      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setPaymentStatus('error');
      setStatusMessage('Error checking payment status. Please contact support.');
    }
  };

  const handlePurchase = async () => {
    setIsProcessing(true);
    
    try {
      const originUrl = window.location.origin;
      const response = await fetch(`${API_URL}/api/presale/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ origin_url: originUrl })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create checkout session');
      }

      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Payment error:', error);
      setStatusMessage(error.message);
      setPaymentStatus('error');
    } finally {
      setIsProcessing(false);
    }
  };

  const scrollToPreSale = () => {
    document.getElementById('presale-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  const purchasePercentage = Math.min((purchaseCount / 10000) * 100, 100);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 text-gray-700 hover:text-blue-600 transition-colors">
              <ArrowLeft className="w-5 h-5" />
              <span className="font-semibold">Back to Home</span>
            </Link>
            <h2 className="text-2xl font-black text-gray-900">FltrLnk</h2>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-blue-900 via-blue-800 to-purple-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-block bg-white/20 text-white px-6 py-2 rounded-full mb-6 text-sm font-bold">
            FOR BUSINESS USERS
          </div>
          <h1 className="text-5xl lg:text-6xl font-black mb-6">
            FltrLite for <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-400">Business</span>
          </h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto mb-8">
            FltrLite is a premium subscription service that authenticates your business and helps maximize your visibility on the platform. Stand out from competitors with verified business status and enhanced discovery features that put you in front of the right customers.
          </p>
        </div>
      </section>

      {/* What is FltrLnk Section */}
      <section className="py-20 px-4 bg-white" data-testid="what-is-fltrlnk-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-block bg-blue-100 text-blue-700 px-6 py-2 rounded-full mb-6 text-sm font-bold">
              WHAT IS FLTRLNK?
            </div>
            <h2 className="text-4xl lg:text-5xl font-black text-gray-900 mb-4">
              The Next-Gen Discovery Platform for Businesses
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              FltrLnk is a next-generation user discovery platform where people find businesses, opportunities, and connections through swipe-based interfaces and GPS-enabled maps. For businesses, it's a brand-new channel to get discovered by the right customers — right when they're looking.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-blue-50 p-6 rounded-2xl border-2 border-blue-100 hover:border-blue-300 transition-colors">
              <div className="w-14 h-14 bg-blue-600 rounded-2xl flex items-center justify-center mb-4">
                <Compass className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Get Discovered</h3>
              <p className="text-gray-600 text-sm">Appear in swipe feeds, searches, and the GPS map so nearby customers find your business effortlessly.</p>
            </div>

            <div className="bg-purple-50 p-6 rounded-2xl border-2 border-purple-100 hover:border-purple-300 transition-colors">
              <div className="w-14 h-14 bg-purple-600 rounded-2xl flex items-center justify-center mb-4">
                <Shield className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Build Trust</h3>
              <p className="text-gray-600 text-sm">A verified business badge signals authenticity and helps customers choose you with confidence.</p>
            </div>

            <div className="bg-green-50 p-6 rounded-2xl border-2 border-green-100 hover:border-green-300 transition-colors">
              <div className="w-14 h-14 bg-green-600 rounded-2xl flex items-center justify-center mb-4">
                <MessageSquare className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Engage Directly</h3>
              <p className="text-gray-600 text-sm">Chat with interested customers in real time and turn discovery into real connections and sales.</p>
            </div>

            <div className="bg-amber-50 p-6 rounded-2xl border-2 border-amber-100 hover:border-amber-300 transition-colors">
              <div className="w-14 h-14 bg-amber-500 rounded-2xl flex items-center justify-center mb-4">
                <Zap className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Boost Visibility</h3>
              <p className="text-gray-600 text-sm">Priority placement and User Boosts push your profile in front of more of the right people.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Tiers */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Choose Your Plan</h2>
            <p className="text-gray-600">Select the FltrLite plan that best fits your business needs</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            
            {/* FltrLite Starter */}
            <div className="bg-white rounded-3xl shadow-xl overflow-hidden border-2 border-blue-200 hover:border-blue-500 transition-colors">
              <div className="bg-gradient-to-r from-blue-600 to-blue-600 p-6 text-white">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <Shield className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold">FltrLite Starter</h3>
                    <p className="text-blue-100 text-sm">Essential visibility</p>
                  </div>
                </div>
              </div>
              
              <div className="p-6">
                <div className="text-center mb-6">
                  <div className="text-4xl font-black text-gray-900">$9.99</div>
                  <p className="text-gray-500 font-semibold">Per Month</p>
                  <div className="inline-block bg-blue-100 text-blue-700 px-4 py-1 rounded-full text-xs font-bold mt-3">
                    CANCEL ANYTIME
                  </div>
                </div>

                <ul className="space-y-3 mb-6">
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-sm">Business Profile Verification with Badge</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-sm">100 Monthly Business Chats</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-sm">Priority Visibility in Algorithms</span>
                  </li>
                </ul>

                <p className="text-xs text-gray-500 text-center italic">*Only available through in app purchase</p>
              </div>
            </div>

            {/* FltrLite Basic */}
            <div className="bg-white rounded-3xl shadow-xl overflow-hidden border-2 border-purple-200 hover:border-purple-400 transition-colors">
              <div className="bg-gradient-to-r from-purple-500 to-purple-600 p-6 text-white">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <Sparkles className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold">FltrLite Basic</h3>
                    <p className="text-purple-100 text-sm">Enhanced engagement</p>
                  </div>
                </div>
              </div>
              
              <div className="p-6">
                <div className="text-center mb-6">
                  <div className="text-4xl font-black text-gray-900">$39.99</div>
                  <p className="text-gray-500 font-semibold">Per Month</p>
                  <div className="inline-block bg-purple-100 text-purple-700 px-4 py-1 rounded-full text-xs font-bold mt-3">
                    CANCEL ANYTIME
                  </div>
                </div>

                <ul className="space-y-3 mb-6">
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-sm">Business Profile Verification with Badge</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-sm">500 Monthly Business Chats</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-sm">5 Monthly User Boost Packs (8 boosts each)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-sm">Priority Visibility in Algorithms</span>
                  </li>
                </ul>

                <p className="text-xs text-gray-500 text-center italic">*Only available through in app purchase</p>
              </div>
            </div>

            {/* FltrLite Pro */}
            <div className="bg-white rounded-3xl shadow-xl overflow-hidden border-2 border-green-200 hover:border-green-400 transition-colors">
              <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-6 text-white">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <Crown className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold">FltrLite Pro</h3>
                    <p className="text-green-100 text-sm">Maximum impact</p>
                  </div>
                </div>
              </div>
              
              <div className="p-6">
                <div className="text-center mb-6">
                  <div className="text-4xl font-black text-gray-900">$59.99</div>
                  <p className="text-gray-500 font-semibold">Per Month</p>
                  <div className="inline-block bg-green-100 text-green-700 px-4 py-1 rounded-full text-xs font-bold mt-3">
                    CANCEL ANYTIME
                  </div>
                </div>

                <ul className="space-y-3 mb-6">
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-sm">Business Profile Verification with Badge</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-sm font-semibold">Unlimited Business Chats</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-sm">10 Monthly User Boost Packs (8 boosts each)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-sm">Priority Visibility in Algorithms</span>
                  </li>
                </ul>

                <p className="text-xs text-gray-500 text-center italic">*Only available through in app purchase</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who Should Join Section */}
      <section className="py-20 px-4 bg-white" data-testid="who-should-join-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-block bg-blue-100 text-blue-700 px-6 py-2 rounded-full mb-6 text-sm font-bold">
              WHO SHOULD JOIN
            </div>
            <h2 className="text-4xl lg:text-5xl font-black text-gray-900 mb-4">Ideal for Small Businesses</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              FltrLnk is built to help small, local businesses get discovered. If you serve customers in your community, you'll thrive here.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {[
              { icon: Utensils, label: 'Restaurants' },
              { icon: Utensils, label: 'Food Trucks' },
              { icon: Coffee, label: 'Cafes' },
              { icon: Scissors, label: 'Barbers' },
              { icon: Sparkle, label: 'Salons & Spas' },
              { icon: Zap, label: 'Fitness Studios & Gyms' },
              { icon: Star, label: 'Escape Rooms & Mini-Golf' },
              { icon: Star, label: 'Art & Pottery Studios' },
              { icon: Briefcase, label: 'Tattoo & Piercing Shops' },
              { icon: Briefcase, label: 'Auto Detailing & Repair' },
              { icon: Briefcase, label: 'Photographers & Event Planners' },
              { icon: Sparkle, label: 'Nail Techs & Estheticians' },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="bg-gray-50 rounded-2xl p-5 border border-gray-100 hover:border-blue-300 hover:bg-blue-50 transition-colors text-center">
                  <Icon className="w-8 h-8 text-blue-600 mx-auto mb-3" />
                  <p className="text-sm font-bold text-gray-800">{item.label}</p>
                </div>
              );
            })}
          </div>
          <p className="text-center text-gray-500 mt-8 text-sm italic">...and many more niche activity & service businesses everywhere.</p>
        </div>
      </section>

      {/* How Your Profile Will Look Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-gray-50 to-blue-50" data-testid="profile-look-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <div className="inline-block bg-purple-100 text-purple-700 px-6 py-2 rounded-full mb-6 text-sm font-bold">
              YOUR BUSINESS PROFILE
            </div>
            <h2 className="text-4xl lg:text-5xl font-black text-gray-900 mb-4">How Your Profile Will Look</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Your business shows up across FltrLnk in beautiful, easy-to-explore profiles — here's exactly what users will see.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <PhoneShot
              imageUrl="https://customer-assets.emergentagent.com/job_3b5ca5ac-4a5a-4d76-aad1-9c866f3c25e6/artifacts/5su9akgd_IMG_2569.jpeg"
              label="FltrRadar Mini Profile"
              sublabel="When users find you on the map, they instantly see all the key details of your business — distance, category, address, hours, contact info, and more."
            />
            <PhoneShot
              imageUrl="https://customer-assets.emergentagent.com/job_3b5ca5ac-4a5a-4d76-aad1-9c866f3c25e6/artifacts/8mj8gxvq_IMG_2644.jpeg"
              label="FltrScreen Mini Profile"
              sublabel="While swiping on the FltrScreen, users can quickly tap into this mini profile to get a fast snapshot of your business at a glance."
            />
            <PhoneShot
              imageUrl="https://customer-assets.emergentagent.com/job_3b5ca5ac-4a5a-4d76-aad1-9c866f3c25e6/artifacts/6i0kpguo_IMG_2645.jpeg"
              label="User Profile"
              sublabel="Users can open your full profile to view all your content — your feed, MOODZ, SLIDEZ, BLOGZ, links, and everything that makes your business shine."
            />
          </div>
        </div>
      </section>

      {/* How Users Discover You Section */}
      <section className="py-20 px-4 bg-white" data-testid="discover-you-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <div className="inline-block bg-green-100 text-green-700 px-6 py-2 rounded-full mb-6 text-sm font-bold">
              GET DISCOVERED
            </div>
            <h2 className="text-4xl lg:text-5xl font-black text-gray-900 mb-4">How Users Discover You</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              FltrLnk puts your business in front of the right people through dynamic, location-aware discovery features that customers actually love to use.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-12">
            <PhoneShot
              imageUrl="https://customer-assets.emergentagent.com/job_3b5ca5ac-4a5a-4d76-aad1-9c866f3c25e6/artifacts/3syt42rk_IMG_2554.jpeg"
              label="FltrRadar Screen"
              sublabel="Users explore an interactive map dotted with businesses near them — discovering you by searching, applying category filters, or changing their location to anywhere they want."
            />
            <PhoneShot
              imageUrl="https://customer-assets.emergentagent.com/job_3b5ca5ac-4a5a-4d76-aad1-9c866f3c25e6/artifacts/bq5u6vsh_IMG_2566.jpeg"
              label="FltrScreen"
              sublabel="Users swipe, search, filter, and change locations on the FltrScreen to find businesses like yours — turning discovery into a fun, engaging experience."
            />
          </div>

          <div className="max-w-3xl mx-auto bg-blue-50 rounded-2xl p-8 border-2 border-blue-100 text-center">
            <MessageSquare className="w-10 h-10 text-blue-600 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-gray-900 mb-2">Business Chats</h3>
            <p className="text-gray-700">
              Start conversations with potential customers directly through FltrLnk. Answer questions, take requests, and build real relationships — all from one inbox, turning discovery into loyal, paying customers.
            </p>
          </div>
        </div>
      </section>

      {/* User Boost Types Section */}
      <section className="py-16 px-4 bg-gradient-to-br from-purple-50 to-blue-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">8 User Boost Types Included</h2>
            <p className="text-gray-600">Each User Boost Pack includes all 8 boost types to maximize your visibility</p>
          </div>
          
          <div className="flex flex-wrap justify-center gap-3">
            <span className="bg-blue-100 text-blue-800 px-4 py-2 rounded-full font-semibold border border-blue-300">FltrBoosts</span>
            <span className="bg-green-100 text-green-800 px-4 py-2 rounded-full font-semibold border border-green-300">LNKBoosts</span>
            <span className="bg-purple-100 text-purple-800 px-4 py-2 rounded-full font-semibold border border-purple-300">MatchBoosts</span>
            <span className="bg-orange-100 text-orange-800 px-4 py-2 rounded-full font-semibold border border-orange-300">GPSBoosts</span>
            <span className="bg-teal-100 text-teal-800 px-4 py-2 rounded-full font-semibold border border-teal-300">LocationBoosts</span>
            <span className="bg-indigo-100 text-indigo-800 px-4 py-2 rounded-full font-semibold border border-indigo-300">UserBoosts</span>
            <span className="bg-pink-100 text-pink-800 px-4 py-2 rounded-full font-semibold border border-pink-300">SearchBoosts</span>
            <span className="bg-yellow-100 text-yellow-800 px-4 py-2 rounded-full font-semibold border border-yellow-300">SpotlightBoosts</span>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-gray-50 to-blue-50" data-testid="faq-section">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-block bg-blue-100 text-blue-700 px-6 py-2 rounded-full mb-6 text-sm font-bold">
              FAQ
            </div>
            <h2 className="text-4xl lg:text-5xl font-black text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-gray-600">Everything you need to know about FltrLite for Business.</p>
          </div>

          <div className="space-y-4">
            {[
              {
                q: 'What is FltrLite for Business?',
                a: 'FltrLite is a premium subscription that authenticates your business and maximizes your visibility on FltrLnk. You get a verified business badge, priority placement in search and discovery, business chats, and User Boosts to put your profile in front of the right customers.'
              },
              {
                q: 'How does FltrLite help my business get discovered?',
                a: 'FltrLnk surfaces your business through swipe-based feeds, keyword search, and a GPS-enabled map. With FltrLite, you get priority visibility in those algorithms plus User Boosts, so more nearby and relevant customers find you first.'
              },
              {
                q: 'What is the difference between the FltrLite tiers?',
                a: 'Each tier unlocks more value: Starter covers verification and priority visibility, Basic adds monthly business chats and User Boost Packs, and Pro adds unlimited business chats and even more monthly boosts. Pick the plan that matches how aggressively you want to grow.'
              }
            ].map((faq, index) => (
              <div
                key={index}
                data-testid={`faq-item-${index}`}
                className="bg-white rounded-2xl border-2 border-gray-200 overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  data-testid={`faq-toggle-${index}`}
                  className="w-full flex items-center justify-between gap-4 p-6 text-left hover:bg-gray-50 transition-colors"
                >
                  <span className="text-lg font-bold text-gray-900">{faq.q}</span>
                  <ChevronDown className={`w-5 h-5 text-blue-600 flex-shrink-0 transition-transform ${openFaq === index ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-6 text-gray-600" data-testid={`faq-answer-${index}`}>
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-4xl font-black text-white mb-6">FltrLnk</h2>
          <div className="flex justify-center gap-6 mb-6 flex-wrap">
            <Link to="/" className="text-gray-400 hover:text-white transition-colors">Home</Link>
            <Link to="/4individuals" className="text-gray-400 hover:text-white transition-colors">4Individuals</Link>
            <Link to="/4business" className="text-gray-400 hover:text-white transition-colors">4Business</Link>
            <Link to="/privacy" className="text-gray-400 hover:text-white transition-colors">Privacy</Link>
            <Link to="/terms" className="text-gray-400 hover:text-white transition-colors">Terms</Link>
            <Link to="/contact" className="text-gray-400 hover:text-white transition-colors">Contact</Link>
          </div>
          <p className="text-gray-500 text-sm">
            © 2025 FltrLnk. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default ForBusiness;
